// CO2 cost allocation (CO2KostAufG) between landlord and tenant
// Source: research/data/co2Kostenaufteilung.json

// 10-step model based on building CO2 emissions per m² per year
export const co2Stufenmodell = [
  { from: 0,  to: 12,   landlordShare: 0.00, energieausweis: 'A+' },
  { from: 12, to: 17,   landlordShare: 0.10, energieausweis: 'A' },
  { from: 17, to: 22,   landlordShare: 0.20, energieausweis: 'B' },
  { from: 22, to: 27,   landlordShare: 0.30, energieausweis: 'C' },
  { from: 27, to: 32,   landlordShare: 0.40, energieausweis: 'D' },
  { from: 32, to: 37,   landlordShare: 0.50, energieausweis: 'D-E' },
  { from: 37, to: 42,   landlordShare: 0.60, energieausweis: 'E' },
  { from: 42, to: 47,   landlordShare: 0.70, energieausweis: 'F' },
  { from: 47, to: 52,   landlordShare: 0.80, energieausweis: 'G' },
  { from: 52, to: null,  landlordShare: 0.95, energieausweis: 'H' },
];

// CO2 price trajectory (EUR per tonne)
export const co2PriceTrajectory = [
  { year: 2024, price: 45 },
  { year: 2025, price: 55 },
  { year: 2026, price: 65 },
  // After 2026: EU ETS2 market-based, use growth rate projection
];

// Default annual growth rate after last fixed price year (for projection)
export const co2PriceGrowthRate = 0.05; // 5% per year assumed after 2026

// Approximate CO2 emissions by Energieausweis class (kg CO2/m²/year)
export const energieausweisMapping = {
  'A+': { co2KgPerSqm: 6 },
  'A':  { co2KgPerSqm: 12 },
  'B':  { co2KgPerSqm: 18 },
  'C':  { co2KgPerSqm: 24 },
  'D':  { co2KgPerSqm: 30 },
  'E':  { co2KgPerSqm: 38 },
  'F':  { co2KgPerSqm: 45 },
  'G':  { co2KgPerSqm: 50 },
  'H':  { co2KgPerSqm: 65 },
};

// Heating types that are subject to CO2 cost allocation
export const heatingTypes = {
  gas:      { subject: true,  co2Factor: 0.201 }, // kg CO2/kWh
  oil:      { subject: true,  co2Factor: 0.266 },
  district: { subject: true,  co2Factor: 0.180 },
  electric: { subject: false, co2Factor: 0 },      // Wärmepumpe exempt
  pellet:   { subject: false, co2Factor: 0 },      // Biomass exempt
};

/**
 * Get CO2 price for a given calendar year
 * Uses fixed trajectory, then applies growth rate for future years
 */
export function getCO2Price(calendarYear) {
  const fixed = co2PriceTrajectory.find(p => p.year === calendarYear);
  if (fixed) return fixed.price;

  const lastFixed = co2PriceTrajectory[co2PriceTrajectory.length - 1];
  if (calendarYear < lastFixed.year) {
    // Before our data — use earliest known price
    return co2PriceTrajectory[0].price;
  }

  // After last fixed year: grow at assumed rate
  const yearsAfter = calendarYear - lastFixed.year;
  return lastFixed.price * Math.pow(1 + co2PriceGrowthRate, yearsAfter);
}

/**
 * Calculate landlord's annual CO2 cost share
 * @param {string} energieausweis - Energy class (A+ through H)
 * @param {string} heatingType - gas, oil, district, electric, pellet
 * @param {number} squareMeters - Property size in m²
 * @param {number} calendarYear - Year for CO2 price lookup
 * @returns {number} Annual landlord CO2 cost in EUR
 */
export function calculateLandlordCO2Cost(energieausweis, heatingType, squareMeters, calendarYear) {
  const heating = heatingTypes[heatingType];
  if (!heating || !heating.subject) return 0;

  const classData = energieausweisMapping[energieausweis];
  if (!classData) return 0;

  const co2KgPerSqm = classData.co2KgPerSqm;
  const totalCO2Tonnes = (co2KgPerSqm * squareMeters) / 1000;

  // Find landlord share from Stufenmodell
  let landlordShare = 0;
  for (const step of co2Stufenmodell) {
    if (co2KgPerSqm >= step.from && (step.to === null || co2KgPerSqm < step.to)) {
      landlordShare = step.landlordShare;
      break;
    }
  }

  const co2Price = getCO2Price(calendarYear);
  const totalCost = totalCO2Tonnes * co2Price;

  return totalCost * landlordShare;
}
